import HiddenInput from "@/components/form/components/HiddenInput";
import LabelWithTooltip from "@/components/form/labels/LabelWithTooltip";
import { InputNumber } from "@/components/InputNumber";
import { resolveFormValidationError, setErrorsToForm } from "@/utils/formUtils";
import { validations } from "@/utils/validationUtils";
import { Checkbox, Col, Divider, Form, Input, Modal, Row } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, useNavigate } from "react-router";
import {
  useCreateCommissionsSettingsLevel,
  useUpdateCommissionsSettingsLevel,
} from "../../../../../common/api/queries";
import { ModalSizes, rowGutter } from "../../../../../common/constants";
import { useIsCurrentAgentTypePlus } from "../../../../auth/hooks";
import CommissionsLevelSelect from "../../../../enumerations/components/form/CommissionsLevelSelect";
import { CommissionsSettingsRankSelect } from "../../../../enumerations/components/form/CommissionsSettingsRankSelect";
import { CommissionsSettingsLevelType } from "../../enums";
import { COMMISSIONS_LEVEL_ROUTE_PATHS } from "../../paths";
import { CommissionsSettingsLevel, CreateUpdateCommissionsSettingsLevel } from "../../types";
import { convertCommissionsSettingsLevelToCreateUpdateObject } from "../../utils";
import { SelectCommissionsSettingsLevelType } from "../SelectCommissionsSettingsLevelType";

interface Props {
  open: boolean;
  level?: CommissionsSettingsLevel;
  onFormCancel: () => void;
}

export const CommissionsLevelForm = ({ open, level, onFormCancel }: Props) => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const isCurrentAgentTypePlus = useIsCurrentAgentTypePlus();

  const [form] = Form.useForm<CreateUpdateCommissionsSettingsLevel>();
  const type = Form.useWatch("type", form);
  const promotionPointsRatio1 = Form.useWatch("promotionPointsRatio1", form);
  const promotionPointsRatio2 = Form.useWatch("promotionPointsRatio2", form);

  const createCommissionsSettingsLevel = useCreateCommissionsSettingsLevel();
  const updateCommissionsSettingsLevel = useUpdateCommissionsSettingsLevel();

  useEffect(() => {
    if (!open) {
      return;
    }

    if (level) {
      form.setFieldsValue(convertCommissionsSettingsLevelToCreateUpdateObject(level));
    } else if (isCurrentAgentTypePlus) {
      form.setFieldValue("type", CommissionsSettingsLevelType.CAREER);
    }
  }, [open, level, form]);

  const handleFormSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        if (level) {
          updateCommissionsSettingsLevel.mutate(
            { id: level.id, object: values },
            {
              onError: (error) => setErrorsToForm(form, "commissions.level.attrs", error.violations),
              onSuccess: () => {
                onFormCancel();
              },
            },
          );
        } else {
          createCommissionsSettingsLevel.mutate(values, {
            onError: (error) => setErrorsToForm(form, "commissions.level.attrs", error.violations),
            onSuccess: (data) => {
              navigate(generatePath(COMMISSIONS_LEVEL_ROUTE_PATHS.detail.to, { id: data.id }));
              onFormCancel();
            },
          });
        }
      })
      .catch(resolveFormValidationError);
  };

  return (
    <Modal
      width={ModalSizes.LARGE}
      open={open}
      title={level ? t("commissions.level.titles.updateLevel") : t("commissions.level.titles.createLevel")}
      okText={t("common.save")}
      cancelText={t("common.cancel")}
      confirmLoading={createCommissionsSettingsLevel.isPending || updateCommissionsSettingsLevel.isPending}
      afterClose={() => form.resetFields()}
      onOk={handleFormSubmit}
      onCancel={onFormCancel}
      mask={{ closable: false }}
    >
      <Form form={form} layout="vertical" name="commissionsSettingsLevelForm">
        <HiddenInput name="optimisticLockVersion" />

        <Row gutter={rowGutter}>
          <Col span={8}>
            <Form.Item name="type" label={t("commissions.level.enums.type._label")} rules={[validations.notNull]}>
              <SelectCommissionsSettingsLevelType
                dataSource={
                  isCurrentAgentTypePlus
                    ? [CommissionsSettingsLevelType.CAREER]
                    : [CommissionsSettingsLevelType.BASIC, CommissionsSettingsLevelType.POINT]
                }
              />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              name="code"
              label={t("commissions.level.attrs.code")}
              rules={[validations.notNull, validations.size(1, 255)]}
            >
              <Input />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              name="name"
              label={t("commissions.level.attrs.name")}
              rules={[validations.notNull, validations.size(1, 255)]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={rowGutter}>
          <Col span={8}>
            <Form.Item name="category" label={t("commissions.level.attrs.category")} rules={[validations.size(1, 255)]}>
              <Input />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              name="targetSubordinatesCount"
              label={t("commissions.level.attrs.targetSubordinatesCount")}
              rules={[validations.minNumber(1)]}
            >
              <InputNumber />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              name="autoAssigned"
              rules={[validations.notNull]}
              initialValue={false}
              className="form-item-without-label"
              valuePropName="checked"
            >
              <Checkbox style={{ maxHeight: 22 }}>
                <LabelWithTooltip
                  label={t("commissions.level.attrs.autoAssigned")}
                  tooltip={t("commissions.level.helpers.autoAssignedHint")}
                />
              </Checkbox>
            </Form.Item>
          </Col>
        </Row>

        {type !== CommissionsSettingsLevelType.BASIC ? (
          <Row gutter={rowGutter}>
            <Col span={24}>
              <CommissionsLevelSelect
                formItemProps={{
                  name: "nextLevelId",
                  label: t("commissions.level.attrs.nextLevelId"),
                  rules: [validations.none],
                }}
                selectProps={{ allowClear: true }}
                optionsProps={{ filterType: type, filter: (l) => level?.id !== l.id }}
              />
            </Col>
          </Row>
        ) : undefined}

        {type === CommissionsSettingsLevelType.CAREER ? (
          <>
            <Divider titlePlacement="start" className="margin-top-small">
              {t("commissions.level.sections.promotionParameters")}
            </Divider>

            <Row gutter={rowGutter}>
              <Col span={8}>
                <Form.Item
                  name="promotionPoints"
                  label={t("commissions.level.attrs.promotionPoints")}
                  rules={[validations.notNull, validations.minNumber(0)]}
                >
                  <InputNumber min={0} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="promotionPointsRatio1"
                  label={t("commissions.level.attrs.promotionPointsRatio")}
                  rules={[
                    validations.minNumber(1),
                    validations.maxNumber(100),
                    ...(promotionPointsRatio1 || promotionPointsRatio2
                      ? [validations.notNull, validations.sumOneHundred([promotionPointsRatio1, promotionPointsRatio2])]
                      : [validations.none]),
                  ]}
                  dependencies={["promotionPointsRatio2"]}
                >
                  <InputNumber max={100} min={1} placeholder={t("common.from")} addon="percentage" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="promotionPointsRatio2"
                  className="form-item-without-label"
                  rules={[
                    validations.minNumber(1),
                    validations.maxNumber(100),
                    ...(promotionPointsRatio1 || promotionPointsRatio2
                      ? [validations.notNull, validations.sumOneHundred([promotionPointsRatio1, promotionPointsRatio2])]
                      : [validations.none]),
                  ]}
                  dependencies={["promotionPointsRatio1"]}
                >
                  <InputNumber max={100} min={1} placeholder={t("common.to")} addon="percentage" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={rowGutter}>
              <Col span={24}>
                <Form.Item
                  name="requiredPromotionRankId"
                  label={t("commissions.level.attrs.requiredPromotionRankId")}
                  rules={[validations.none]}
                >
                  <CommissionsSettingsRankSelect allowClear />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={rowGutter}>
              <Col span={8}>
                <Form.Item
                  name="promotionFinancialAcademyRequired"
                  valuePropName="checked"
                  rules={[validations.none]}
                  initialValue={false}
                >
                  <Checkbox>{t("commissions.level.attrs.promotionFinancialAcademyRequired")}</Checkbox>
                </Form.Item>
              </Col>

              <Col span={8}>
                <Form.Item
                  name="promotionPersonalAcademyRequired"
                  valuePropName="checked"
                  rules={[validations.none]}
                  initialValue={false}
                >
                  <Checkbox>{t("commissions.level.attrs.promotionPersonalAcademyRequired")}</Checkbox>
                </Form.Item>
              </Col>

              <Col span={8}>
                <Form.Item
                  name="promotionBrokerStatusRequired"
                  valuePropName="checked"
                  rules={[validations.none]}
                  initialValue={false}
                >
                  <Checkbox>{t("commissions.level.attrs.promotionBrokerStatusRequired")}</Checkbox>
                </Form.Item>
              </Col>
            </Row>
          </>
        ) : undefined}
      </Form>
    </Modal>
  );
};
